import { CrudList } from '../shared/CrudPage';
import { payrollService, enumLabel } from '@kss/api';
import type { Payroll, PayrollCreate, PayrollFilter } from '@kss/api';
import { StatusBadge } from '@kss/ui';

const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Payroll is a **company-monthly batch** in the live API (`{ month,
 * year, totalGross, totalDeductions, totalNet }`), NOT a per-employee
 * record. The grid previously read legacy `period` / `employeeId` /
 * `basicSalary` / `netSalary` fields — none of which exist on real
 * rows — so every cell rendered empty after a save.
 *
 * Bug ref: AI Agent (round 3) — "Payroll: filled data is not showing
 * in table." Columns now mirror NewPayroll.tsx, which is wired to the
 * live schema.
 */
export default function AllPayrolls() {
  return (
    <CrudList<Payroll, PayrollCreate, PayrollFilter>
      title="All payrolls" newLabel="New payroll" service={payrollService}
      idKey="payrollId" labelKey="payrollNumber" basePath="/panel/payrolls"
      // Newest payroll batch on top — order-like grid. Ref: ORDER_WORKFLOW_AUDIT #6.
      initialSortStatus={{ columnAccessor: 'processedDate', direction: 'desc' }}
      columns={[
        { accessor: 'payrollNumber', title: 'Number', sortable: true, filterable: true, width: 160 },
        { accessor: 'month', title: 'Period', sortable: true,
          render: (r) => {
            const m = Number(r.month ?? 0);
            const y = r.year ?? '';
            return m >= 1 && m <= 12 ? `${MONTHS[m]} ${y}` : '—';
          },
        },
        { accessor: 'processedDate', title: 'Processed', sortable: true,
          render: (r) => {
            const v = r.processedDate ?? r.processedAt;
            return v ? new Date(v).toLocaleDateString() : '—';
          },
        },
        { accessor: 'totalGross', title: 'Gross (₹)', sortable: true, textAlign: 'right',
          render: (r) => Number(r.totalGross ?? 0).toLocaleString('en-IN') },
        { accessor: 'totalDeductions', title: 'Deductions (₹)', sortable: true, textAlign: 'right',
          render: (r) => Number(r.totalDeductions ?? 0).toLocaleString('en-IN') },
        { accessor: 'totalNet', title: 'Net (₹)', sortable: true, textAlign: 'right',
          render: (r) => Number(r.totalNet ?? 0).toLocaleString('en-IN') },
        { accessor: 'status', title: 'Status', sortable: true,
          render: (r) => <StatusBadge status={enumLabel('PayrollStatus', typeof r.status === 'number' ? r.status : Number(r.status))} /> },
      ]}
    />
  );
}
